import { useEffect, useRef, useState } from 'react'

import HowItWorks from './HowItWorks'
import { VaultHero, VaultStats } from './VaultHeader'

import { PageHead } from '@/common/components/PageHead'
import { useValueTokens } from '@/contexts/VaultTokensContext'
import { LiquidStaking } from '@/modules/components/LiquidStaking'
import StakingForm from '@/modules/components/StakingForm/StakingForm'

export default function VaultPage() {
  const { currentToken } = useValueTokens()

  // Floating CTA (mobile): visible until the deposit module scrolls into view, then it steps aside.
  const depositRef = useRef<HTMLDivElement>(null)
  const [ctaVisible, setCtaVisible] = useState(true)
  useEffect(() => {
    const el = depositRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(([entry]) => setCtaVisible(!entry.isIntersecting), {
      threshold: 0.12,
    })
    io.observe(el)
    return () => io.disconnect()
  }, [currentToken])

  if (!currentToken) {
    return (
      <div
        className="flex w-full items-center justify-center tabular-nums"
        style={{ minHeight: '60vh', color: 'var(--muted)', fontSize: 22 }}
      >
        —
      </div>
    )
  }

  const isAvax = currentToken.value === 'avax'
  const action = isAvax ? 'Deposit' : 'Withdraw'
  const ctaTitle = isAvax
    ? `Put your ${currentToken.displayName} to work`
    : `Withdraw your ${currentToken.displayName}`
  const ctaSub = isAvax
    ? 'Non-custodial · redeem anytime'
    : 'Withdrawals open · GoGoPool wound down'

  return (
    <>
      <PageHead
        append={false}
        description={`${currentToken.displayName} Vault by SeaFi: earn yield on idle ${currentToken.displayName} with a non-custodial ERC-4626 vault. See how it works, live APY and liquidity, then deposit or withdraw.`}
        favicon="/favicon.svg"
        name={`${currentToken.displayName} Vault`}
      />

      {/* hero banner — full width across the top (matches Treasury) */}
      <VaultHero />

      {/* action zone: stats + how-it-works scroll in the left column while the deposit rail
          stays sticky in the right column (matches Treasury's detail layout). The grid lives in
          .vault-zone (plain media query) to dodge Tailwind v3's arbitrary grid-cols limitation. */}
      <div className="vault-zone lg:mt-10 mt-8">
        <div className="lg:gap-8 flex flex-col gap-6">
          <VaultStats />
          <HowItWorks />
        </div>

        <div className="vault-zone__rail" id="deposit" ref={depositRef}>
          {isAvax ? <StakingForm /> : <LiquidStaking />}
        </div>
      </div>

      {/* floating call-to-action — mobile only, sits above the bottom nav, hides once the deposit
          module is on screen. Drives the user to the action that matters. */}
      <a
        aria-hidden={!ctaVisible}
        className={`vault-cta ${ctaVisible ? '' : 'is-hidden'}`}
        href="#deposit"
        tabIndex={ctaVisible ? 0 : -1}
      >
        <span className="vault-cta-copy">
          <span className="vault-cta-sub">{ctaSub}</span>
          <span className="vault-cta-title">{ctaTitle}</span>
        </span>
        <span className="vault-cta-btn">
          {action}
          <svg
            aria-hidden="true"
            fill="none"
            height="16"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            width="16"
          >
            <path d="M12 5v14M6 13l6 6 6-6" />
          </svg>
        </span>
      </a>
    </>
  )
}
